import { Appointment, TaskListItem, TimeManagementListItem } from '@myapp/contract'

export const GERMAN_WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'] as const

export function getMondayOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dow = d.getDay() // 0=Sun
  const diff = dow === 0 ? -6 : 1 - dow
  d.setDate(d.getDate() + diff)
  return d
}

export function getWeekDays(monday: Date): string[] {
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday)
    d.setDate(d.getDate() + i)
    return toDateKey(d)
  })
}

export function getWeekRange(monday: Date): { from: string; to: string } {
  const friday = new Date(monday)
  friday.setDate(friday.getDate() + 4)
  return {
    from: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0).toISOString(),
    to: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 23, 59, 59, 999).toISOString(),
  }
}

export function groupAppointmentsByDay(
  appointments: Appointment[],
  weekDays: string[],
): Map<string, Appointment[]> {
  const map = new Map<string, Appointment[]>()
  for (const day of weekDays) {
    map.set(day, [])
  }
  for (const appt of appointments) {
    const d = new Date(appt.actualStart ?? appt.plannedStart)
    const key = toDateKey(d)
    const list = map.get(key)
    if (list) list.push(appt)
  }
  weekDays.forEach((day) => {
    const list = map.get(day)
    if (list) {
      list.sort((a: Appointment, b: Appointment) => new Date(a.actualStart ?? a.plannedStart).getTime() - new Date(b.actualStart ?? b.plannedStart).getTime())
    }
  })
  return map
}

const monthFormatter = new Intl.DateTimeFormat('de-DE', { month: 'long' })

export function formatWeekLabel(monday: Date, friday: Date): string {
  const monMonth = monthFormatter.format(monday)
  const friMonth = monthFormatter.format(friday)
  const year = friday.getFullYear()

  if (monMonth === friMonth) {
    return `${monday.getDate()}. – ${friday.getDate()}. ${friMonth} ${year}`
  }
  return `${monday.getDate()}. ${monMonth} – ${friday.getDate()}. ${friMonth} ${year}`
}

function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function groupTimeManagementByDay(
  entries: TimeManagementListItem[],
  weekDays: string[],
): Map<string, TimeManagementListItem[]> {
  const map = new Map<string, TimeManagementListItem[]>()
  for (const day of weekDays) map.set(day, [])

  for (const entry of entries) {
    const entryStart = new Date(entry.startTime)
    const entryEnd = new Date(entry.endTime)
    for (const day of weekDays) {
      const dayStart = new Date(day + 'T00:00:00')
      const dayEnd = new Date(day + 'T23:59:59.999')
      if (entryStart <= dayEnd && entryEnd >= dayStart) {
        map.get(day)!.push(entry)
      }
    }
  }
  return map
}

export function groupTasksByDay(
  tasks: TaskListItem[],
  weekDays: string[],
): Map<string, TaskListItem[]> {
  const map = new Map<string, TaskListItem[]>()
  for (const day of weekDays) map.set(day, [])

  for (const task of tasks) {
    if (!task.plannedStart) continue
    const key = toDateKey(new Date(task.plannedStart))
    const list = map.get(key)
    if (list) list.push(task)
  }
  return map
}

export function filterSchedulableAppointments(appointments: Appointment[]): Appointment[] {
  return appointments.filter((a) => !a.needsRescheduling)
}

export function filterSchedulableTimeManagement(entries: TimeManagementListItem[]): TimeManagementListItem[] {
  return entries.filter((e) => !e.needsRescheduling)
}

export function filterSchedulableTasks(tasks: TaskListItem[]): TaskListItem[] {
  return tasks.filter((t) => !t.needsRescheduling)
}

export function isWorkingDay(workingDays: number[], dayIso: string): boolean {
  const dow = new Date(dayIso).getDay()
  // JS getDay(): 0=Sun,1=Mon..6=Sat -> DB workingDays: 1=Mon..7=Sun
  const mapped = dow === 0 ? 7 : dow
  return workingDays.includes(mapped)
}
